import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConfigStore, TASK_LABELS } from '../stores/configStore';
import { useThemeStore } from '../stores/themeStore';
import { useUiStore } from '../stores/uiStore';
import type { Font, Theme } from '../stores/themeStore';
import type {
  ModelRegistryEntry,
  TaskTierConfig,
  TierInfo,
  ModelPricingMap,
  OpenRouterCatalogEntry,
  RefreshReport,
} from '../types';
import type { RoutingPresets, CostReport } from '../types/config';
import BatchStatusPanel from '../components/BatchStatusPanel';
import type { BatchJob } from '../components/BatchStatusPanel';
import { api } from '../services/api';
import { copyText } from '../utils/clipboard';
import './SettingsPage.css';

/** Shape of the config object returned by the settings API */
interface ProviderStatus {
  configured?: boolean;
}

interface SettingsConfig {
  providers?: {
    anthropic?: ProviderStatus;
    openai?: ProviderStatus;
    openrouter?: ProviderStatus;
    ollama?: ProviderStatus;
    elevenlabs?: ProviderStatus;
  };
  airtable?: ProviderStatus;
  [key: string]: unknown;
}

interface CapabilityKey {
  key: keyof ModelRegistryEntry['capabilities'];
  label: string;
}

interface ProviderOption {
  id: ModelRegistryEntry['provider'];
  label: string;
}

interface TaskAssignmentsTableProps {
  taskConfig: TaskTierConfig;
  tiers: TierInfo[];
  registry: ModelRegistryEntry[];
  inheritsSentinel: string;
  onChange: (taskName: string, tier: string) => void;
}

interface ModelRegistryListProps {
  registry: ModelRegistryEntry[];
  pricing: ModelPricingMap;
  catalog: OpenRouterCatalogEntry[];
  tasksUsingModel: (displayName: string) => string[];
  refreshing: boolean;
  refreshReport: RefreshReport | null;
  refreshError: string | null;
  onRefresh: () => Promise<void>;
  onAdd: (entry: ModelRegistryEntry) => Promise<void>;
  onAddOpenRouter: (id: string, displayName?: string) => Promise<void>;
  onUpdate: (displayName: string, patch: Partial<ModelRegistryEntry>) => Promise<void>;
  onDelete: (displayName: string) => void;
}

/** Format a $/MTok price for display, or an em dash when unknown. */
function fmtPrice(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n === 0) return 'free';
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(n < 1 ? 3 : 2)}`;
}

interface ModelFormProps {
  initial: ModelRegistryEntry;
  existingNames: string[];
  lockName?: boolean;
  onCancel: () => void;
  onSubmit: (entry: ModelRegistryEntry) => Promise<void>;
}

const CAPABILITY_KEYS: CapabilityKey[] = [
  { key: 'web_search', label: 'Web search' },
  { key: 'prompt_caching', label: 'Prompt caching' },
  { key: 'vision', label: 'Vision' },
];

const PROVIDER_OPTIONS: ProviderOption[] = [
  { id: 'anthropic', label: 'Anthropic Claude' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'openrouter', label: 'OpenRouter' },
  { id: 'ollama', label: 'Ollama (Local)' },
];

function emptyModelDraft(): ModelRegistryEntry {
  return {
    displayName: '',
    provider: 'anthropic',
    modelId: '',
    capabilities: { web_search: false, prompt_caching: false, vision: false },
  };
}

export default function SettingsPage() {
  const {
    config,
    loading,
    error,
    ollamaModels,
    autoGenerateNext,
    fetchConfig,
    fetchAutoGenerate,
    toggleAutoGenerate,
    fetchOllamaModels,
    modelRegistry,
    taskConfig,
    tiers,
    inheritsSentinel,
    modelRoutingLoaded,
    modelRoutingError,
    fetchModelRouting,
    updateTaskAssignment,
    addModel,
    updateModel,
    deleteModel,
    tasksUsingModel,
    modelPricing,
    openrouterCatalog,
    refreshing,
    refreshReport,
    refreshError,
    fetchModelPricing,
    fetchOpenRouterCatalog,
    refreshModels,
    addOpenRouterModel,
    presets,
    presetsError,
    fetchPresets,
    savePreset,
    applyPreset,
    deletePreset,
    bannedPhrases,
    qaConfigError,
    fetchQaConfig,
    saveBannedPhrases,
    costReport,
    fetchCost,
  } = useConfigStore();

  const [batches, setBatches] = useState<BatchJob[]>([]);

  const fetchBatches = useCallback(async () => {
    try {
      const data = await api.get('/api/batch') as { batches: BatchJob[] };
      setBatches(data.batches || []);
    } catch {
      // ignore
    }
  }, []);

  const handleCancelBatch = useCallback(async (batchId: string) => {
    try {
      await api.delete(`/api/batch/${batchId}`);
      fetchBatches();
    } catch {
      // ignore
    }
  }, [fetchBatches]);

  useEffect(() => {
    fetchConfig();
    fetchOllamaModels();
    fetchModelRouting();
    fetchAutoGenerate();
    fetchBatches();
    fetchPresets();
    fetchQaConfig();
    fetchCost();
    fetchModelPricing();
    fetchOpenRouterCatalog();
  }, [fetchConfig, fetchOllamaModels, fetchModelRouting, fetchAutoGenerate, fetchBatches, fetchPresets, fetchQaConfig, fetchCost, fetchModelPricing, fetchOpenRouterCatalog]);

  const typedConfig = config as SettingsConfig | null;

  if (loading) return <div className="settings-loading">Loading config...</div>;

  return (
    <div className="settings-page">
      <h1 className="settings-title">Settings</h1>

      {error && <div className="settings-error">{error}</div>}

      {/* Appearance */}
      <AppearanceSection />

      {/* Provider Status.
          Status is KEY PRESENCE (the worker reports !!env.X_API_KEY), not a
          live ping — labeled "Key configured" so it doesn't over-claim.
          Ollama is the exception: its model list is a real reachability check. */}
      <section className="settings-section">
        <h2 className="section-title">Provider Status</h2>
        <p className="section-desc">
          Status shows whether the Worker secret is set (not a live API ping). Ollama shows actual reachability.
        </p>
        <div className="provider-grid">
          <ProviderCard
            name="Anthropic Claude"
            configured={!!typedConfig?.providers?.anthropic?.configured}
            secrets={['ANTHROPIC_API_KEY']}
          />
          <ProviderCard
            name="OpenAI"
            configured={!!typedConfig?.providers?.openai?.configured}
            secrets={['OPENAI_API_KEY']}
          />
          <ProviderCard
            name="OpenRouter"
            configured={!!typedConfig?.providers?.openrouter?.configured}
            secrets={['OPENROUTER_API_KEY']}
          />
          <div className={`provider-card ${typedConfig?.providers?.ollama?.configured ? 'connected' : ''}`}>
            <span className="provider-name">Ollama</span>
            <span className={`provider-status ${ollamaModels.length > 0 ? 'status-ok' : 'status-missing'}`}>
              {ollamaModels.length > 0
                ? `${ollamaModels.length} models available`
                : 'Not running or not configured'}
            </span>
            <span className="provider-key-note">Local endpoint — configured in app config, not a secret.</span>
          </div>
          <ProviderCard
            name="ElevenLabs"
            configured={!!typedConfig?.providers?.elevenlabs?.configured}
            secrets={['ELEVENLABS_API_KEY']}
          />
          <ProviderCard
            name="Airtable"
            configured={!!typedConfig?.airtable?.configured}
            missingText="Add API key + Base ID in Worker secrets"
            secrets={['AIRTABLE_API_KEY', 'AIRTABLE_BASE_ID_CONTENT', 'AIRTABLE_BASE_ID_SOCIAL', 'AIRTABLE_BASE_ID_ADS']}
          />
        </div>
      </section>

      {/* Cost Meter */}
      <CostMeterSection costReport={costReport} />

      {/* Pipeline */}
      <section className="settings-section">
        <h2>Pipeline</h2>
        <div className="settings-field">
          <label className="settings-toggle">
            <input
              type="checkbox"
              checked={autoGenerateNext}
              onChange={(e) => toggleAutoGenerate(e.target.checked)}
            />
            Auto-generate next stage on approval
          </label>
          <p className="settings-help">When enabled, approving a stage automatically starts generating the next stage in the background.</p>
        </div>
        <WordBankAutoChainToggle />
      </section>

      {/* Default directions (2026-10-31) */}
      <DirectionDefaultsSection />

      {/* Social scheduler mode (Chunk 7) */}
      <SocialScheduleModeSection />

      {/* Batch Jobs */}
      <section className="settings-section">
        <BatchStatusPanel batches={batches} onCancel={handleCancelBatch} onRefresh={fetchBatches} />
      </section>

      {/* Model bake-off (Chunk 12) */}
      <section className="settings-section">
        <h2>Model bake-off</h2>
        <p className="settings-help">Compare 2 to 4 models on any prompt, blind, then set the winner as that prompt's model.</p>
        <button className="model-edit-btn" onClick={() => useUiStore.getState().setPage('bakeoff')}>Open Model bake-off</button>
      </section>

      {/* Models — routing layer */}
      <section className="settings-section">
        <h2 className="section-title">Models</h2>
        <p className="section-desc">
          Assign each task to a tier. When Anthropic releases a new model, the tier picks
          it up automatically — no per-task edits needed. Changes save immediately.
        </p>

        {modelRoutingError && <div className="settings-error">{modelRoutingError}</div>}

        {modelRoutingLoaded && (
          <>
            <TaskAssignmentsTable
              taskConfig={taskConfig}
              tiers={tiers}
              registry={modelRegistry}
              inheritsSentinel={inheritsSentinel}
              onChange={updateTaskAssignment}
            />
            <RoutingPresetsSection
              presets={presets}
              error={presetsError}
              onSave={savePreset}
              onApply={applyPreset}
              onDelete={deletePreset}
            />
            <ModelRegistryList
              registry={modelRegistry}
              pricing={modelPricing}
              catalog={openrouterCatalog}
              tasksUsingModel={tasksUsingModel}
              refreshing={refreshing}
              refreshReport={refreshReport}
              refreshError={refreshError}
              onRefresh={refreshModels}
              onAdd={addModel}
              onAddOpenRouter={addOpenRouterModel}
              onUpdate={updateModel}
              onDelete={deleteModel}
            />
          </>
        )}
      </section>

      {/* QA Banned Phrases */}
      <QaBannedPhrasesSection
        bannedPhrases={bannedPhrases}
        error={qaConfigError}
        onSave={saveBannedPhrases}
      />

      {/* Help text — every secret the app actually supports (matches the
          Provider Status detection in the worker's /api/config). */}
      <section className="settings-section">
        <h2 className="section-title">API Keys</h2>
        <p className="section-desc">
          API keys are stored as Cloudflare Worker secrets for security. To update one, run{' '}
          <code>npx wrangler secret put &lt;NAME&gt;</code> in your terminal from the{' '}
          <code>WAW-Content-Engine/workers</code> folder. No redeploy needed.
        </p>
        <div className="secret-list">
          <code>ANTHROPIC_API_KEY</code>
          <code>OPENAI_API_KEY</code>
          <code>OPENROUTER_API_KEY</code>
          <code>ELEVENLABS_API_KEY</code>
          <code>AIRTABLE_API_KEY</code>
          <code>AIRTABLE_BASE_ID_CONTENT</code>
          <code>AIRTABLE_BASE_ID_SOCIAL</code>
          <code>AIRTABLE_BASE_ID_ADS</code>
          <code>AUTH_EMAIL</code>
          <code>AUTH_PASSWORD</code>
        </div>
      </section>
    </div>
  );
}

/**
 * Provider Status card with a "Set / update key" affordance (2026-07-05).
 * API keys are Cloudflare Worker secrets only the operator can write from
 * their terminal — this deliberately shows the exact command to run instead
 * of any input field. NO raw key value is ever accepted, stored, or sent by
 * the app.
 */
function ProviderCard({ name, configured, secrets, missingText }: {
  name: string;
  configured: boolean;
  secrets: string[];
  missingText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);

  useEffect(() => {
    if (!copiedCmd) return;
    const t = window.setTimeout(() => setCopiedCmd(null), 1500);
    return () => window.clearTimeout(t);
  }, [copiedCmd]);

  const handleCopy = useCallback(async (cmd: string) => {
    const ok = await copyText(cmd);
    if (ok) setCopiedCmd(cmd);
  }, []);

  return (
    <div className={`provider-card ${configured ? 'connected' : ''}`}>
      <span className="provider-name">{name}</span>
      <span className={`provider-status ${configured ? 'status-ok' : 'status-missing'}`}>
        {configured ? 'Key configured' : (missingText || 'Add API key in Worker secrets')}
      </span>
      <button className="provider-key-btn" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide' : 'Set / update key'}
      </button>
      {open && (
        <div className="provider-key-panel">
          {secrets.map((s) => {
            const cmd = `npx wrangler secret put ${s}`;
            return (
              <div key={s} className="provider-key-cmd">
                <code>{cmd}</code>
                <button
                  className="provider-key-copy"
                  onClick={() => void handleCopy(cmd)}
                  title="Copy command"
                >
                  {copiedCmd === cmd ? 'Copied!' : 'Copy'}
                </button>
              </div>
            );
          })}
          <p className="provider-key-note">
            Run in your terminal from <code>WAW-Content-Engine/workers</code> and paste the key when prompted. Takes effect immediately — no redeploy needed.
          </p>
        </div>
      )}
    </div>
  );
}

const THEME_OPTIONS: { id: Theme; label: string }[] = [
  { id: 'dark', label: 'Dark' },
  { id: 'light', label: 'Light' },
];

interface FontOption { id: Font; label: string; sample: string }
interface FontGroup { label: string; options: FontOption[] }

const FONT_GROUPS: FontGroup[] = [
  {
    label: 'Serif',
    options: [
      { id: 'palatino', label: 'Palatino', sample: "'Palatino Linotype', 'Book Antiqua', Palatino, serif" },
      { id: 'times', label: 'Times', sample: "'Times New Roman', Times, serif" },
      { id: 'georgia', label: 'Georgia', sample: "Georgia, serif" },
    ],
  },
  {
    label: 'Sans-Serif',
    options: [
      { id: 'arial', label: 'Arial', sample: "Arial, Helvetica, sans-serif" },
      { id: 'helvetica', label: 'Helvetica', sample: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
    ],
  },
  {
    label: 'System',
    options: [
      { id: 'system', label: 'System default', sample: "system-ui, -apple-system, sans-serif" },
    ],
  },
];

function AppearanceSection() {
  const { theme, font, setTheme, setFont } = useThemeStore();

  return (
    <section className="settings-section">
      <h2 className="section-title">Appearance</h2>
      <p className="section-desc">Theme and content font apply across the app. Preferences are saved locally.</p>

      <div className="settings-field">
        <label className="settings-field-label">Theme</label>
        <div className="settings-radio-group">
          {THEME_OPTIONS.map((opt) => (
            <label key={opt.id} className="settings-radio">
              <input
                type="radio"
                name="theme"
                value={opt.id}
                checked={theme === opt.id}
                onChange={() => setTheme(opt.id)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>

      <div className="settings-field">
        <label className="settings-field-label">Content font</label>
        <p className="settings-help">Applies to card body text. UI elements stay in Inter.</p>
        <div className="settings-font-groups">
          {FONT_GROUPS.map((group) => (
            <div key={group.label} className="settings-font-group">
              <div className="settings-font-group-label">{group.label}</div>
              <div className="settings-radio-group settings-font-radio-group">
                {group.options.map((opt) => (
                  <label key={opt.id} className="settings-radio">
                    <input
                      type="radio"
                      name="font"
                      value={opt.id}
                      checked={font === opt.id}
                      onChange={() => setFont(opt.id)}
                    />
                    <span style={{ fontFamily: opt.sample }}>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TIER_LABELS: Record<string, string> = {
  reasoning: 'Reasoning (smartest)',
  balanced: 'Balanced',
  fast: 'Fast (cheapest)',
};

function TaskAssignmentsTable({ taskConfig, tiers, registry, inheritsSentinel, onChange }: TaskAssignmentsTableProps) {
  const taskEntries = Object.entries(TASK_LABELS);
  const tierKeys = new Set<string>(tiers.map((t) => t.tier));
  // A task target may be a tier name or a specific registry model id.
  const modelIds = new Set<string>(
    registry.map((m) => m.id).filter((id): id is string => !!id)
  );

  return (
    <div className="models-subsection">
      <h3 className="subsection-title">Task Assignments</h3>
      <p className="settings-help">
        Assign each task to a tier (auto-tracks the tier's model) or pin it to a specific registry model.
      </p>
      <div className="stage-config-list">
        {taskEntries.map(([taskName, label]) => {
          const current = (taskConfig[taskName] as string) || '';
          const allowInherit = taskName === 'regenerate_compare';
          const isKnown =
            current === inheritsSentinel || tierKeys.has(current) || modelIds.has(current);
          return (
            <div key={taskName} className="stage-config-row">
              <span className="stage-label">{label}</span>
              <select
                className="stage-select model-select"
                value={current}
                onChange={(e) => onChange(taskName, e.target.value)}
              >
                {!isKnown && current && (
                  <option value={current}>{current} (unknown target)</option>
                )}
                {allowInherit && (
                  <option value={inheritsSentinel}>Inherits from originating stage</option>
                )}
                <optgroup label="Tiers">
                  {tiers.map((t) => (
                    <option key={t.tier} value={t.tier}>
                      {TIER_LABELS[t.tier] || t.tier} ({t.displayName})
                    </option>
                  ))}
                </optgroup>
                {registry.some((m) => m.id) && (
                  <optgroup label="Specific models">
                    {registry
                      .filter((m) => m.id)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.displayName} ({m.provider})
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModelRegistryList({
  registry,
  pricing,
  catalog,
  tasksUsingModel,
  refreshing,
  refreshReport,
  refreshError,
  onRefresh,
  onAdd,
  onAddOpenRouter,
  onUpdate,
  onDelete,
}: ModelRegistryListProps) {
  const [adding, setAdding] = useState<boolean>(false);
  const [pickingOpenRouter, setPickingOpenRouter] = useState<boolean>(false);
  const [editing, setEditing] = useState<string | null>(null);

  const existingOpenRouterIds = useMemo(
    () => new Set(registry.filter((m) => m.provider === 'openrouter').map((m) => m.modelId)),
    [registry]
  );

  return (
    <div className="models-subsection">
      <div className="subsection-header">
        <h3 className="subsection-title">Model Registry</h3>
        <div className="model-registry-actions">
          <button className="model-refresh-btn" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing…' : '↻ Refresh Models'}
          </button>
          {!pickingOpenRouter && (
            <button className="model-add-btn" onClick={() => setPickingOpenRouter(true)}>
              + OpenRouter
            </button>
          )}
          {!adding && (
            <button className="model-add-btn" onClick={() => setAdding(true)}>
              + Add Model
            </button>
          )}
        </div>
      </div>

      <p className="settings-help">
        Refresh auto-pulls each cloud provider's current model list (for any provider with an API key set)
        and prices every model from OpenRouter's catalog. Refresh is additive — it never removes models
        or changes tier assignments. Manual add stays as a backup.
      </p>

      {refreshError && <div className="settings-error">{refreshError}</div>}
      {refreshReport && <RefreshReportView report={refreshReport} />}

      {pickingOpenRouter && (
        <OpenRouterPicker
          catalog={catalog}
          existingIds={existingOpenRouterIds}
          onCancel={() => setPickingOpenRouter(false)}
          onPick={async (id, displayName) => {
            await onAddOpenRouter(id, displayName);
            setPickingOpenRouter(false);
          }}
        />
      )}

      {adding && (
        <ModelForm
          initial={emptyModelDraft()}
          existingNames={registry.map((m) => m.displayName)}
          onCancel={() => setAdding(false)}
          onSubmit={async (entry) => {
            await onAdd(entry);
            setAdding(false);
          }}
        />
      )}

      <div className="model-list">
        {registry.length === 0 && !adding && (
          <div className="model-empty">No models in registry. Add one to start routing tasks.</div>
        )}
        {registry.map((entry) => {
          const isEditing = editing === entry.displayName;
          const tasks = tasksUsingModel(entry.displayName);
          const canDelete = tasks.length === 0;

          if (isEditing) {
            return (
              <ModelForm
                key={entry.displayName}
                initial={entry}
                lockName
                existingNames={registry.map((m) => m.displayName)}
                onCancel={() => setEditing(null)}
                onSubmit={async (patch) => {
                  await onUpdate(entry.displayName, patch);
                  setEditing(null);
                }}
              />
            );
          }

          const price = entry.id ? pricing[entry.id] : undefined;

          return (
            <div key={entry.displayName} className="model-row">
              <div className="model-row-main">
                <span className="model-name">{entry.displayName}</span>
                <span className="model-provider">{entry.provider}</span>
                <span className="model-id">{entry.modelId || <em>(no model ID)</em>}</span>
                {entry.id && <span className="model-registry-id">id: {entry.id}</span>}
              </div>
              <div className="model-row-price" title={price ? `Source: ${price.source}${price.matchedId ? ` (${price.matchedId})` : ''}` : 'No price — run Refresh Models'}>
                {entry.provider === 'ollama' ? (
                  <span className="model-price-local">local</span>
                ) : (
                  <>
                    <span className="model-price-val">in {fmtPrice(price?.input)}</span>
                    <span className="model-price-val">out {fmtPrice(price?.output)}</span>
                    {price?.source === 'fallback' && <span className="model-price-src">fallback</span>}
                  </>
                )}
              </div>
              <div className="model-row-caps">
                {CAPABILITY_KEYS.filter((c) => entry.capabilities?.[c.key]).map((c) => (
                  <span key={c.key} className="model-cap">{c.label}</span>
                ))}
              </div>
              <div className="model-row-actions">
                <button className="model-edit-btn" onClick={() => setEditing(entry.displayName)}>
                  Edit
                </button>
                <button
                  className="model-delete-btn"
                  disabled={!canDelete}
                  title={canDelete ? 'Delete model' : `In use by: ${tasks.join(', ')}. Reassign those tasks first.`}
                  onClick={() => onDelete(entry.displayName)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModelForm({ initial, existingNames, lockName, onCancel, onSubmit }: ModelFormProps) {
  const [draft, setDraft] = useState<ModelRegistryEntry>(initial);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);

  const otherNames = useMemo(
    () => existingNames.filter((n) => n !== initial.displayName),
    [existingNames, initial.displayName]
  );

  const submit = async (): Promise<void> => {
    setFormError(null);
    const name = draft.displayName.trim();
    if (!name) {
      setFormError('Display name is required.');
      return;
    }
    if (otherNames.includes(name)) {
      setFormError('A model with that display name already exists.');
      return;
    }
    if (!draft.provider) {
      setFormError('Provider is required.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        ...(draft.id ? { id: draft.id } : {}),
        displayName: name,
        provider: draft.provider,
        modelId: draft.modelId.trim(),
        capabilities: draft.capabilities,
      });
    } catch (err) {
      setFormError((err as Error).message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="model-form">
      {draft.id && (
        <div className="model-form-row">
          <label className="model-form-label">Registry ID</label>
          <input
            type="text"
            className="model-form-input"
            value={draft.id}
            readOnly
            disabled
          />
        </div>
      )}
      <div className="model-form-row">
        <label className="model-form-label">Display Name</label>
        <input
          type="text"
          className="model-form-input"
          value={draft.displayName}
          disabled={lockName}
          onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
          placeholder="e.g. Claude Opus 4.6"
        />
      </div>
      <div className="model-form-row">
        <label className="model-form-label">Provider</label>
        <select
          className="model-form-input"
          value={draft.provider}
          onChange={(e) => setDraft({ ...draft, provider: e.target.value as ModelRegistryEntry['provider'] })}
        >
          {PROVIDER_OPTIONS.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </div>
      <div className="model-form-row">
        <label className="model-form-label">Model ID</label>
        <input
          type="text"
          className="model-form-input"
          value={draft.modelId}
          onChange={(e) => setDraft({ ...draft, modelId: e.target.value })}
          placeholder="e.g. claude-opus-4-6 or llama3:8b"
        />
      </div>
      <div className="model-form-row">
        <span className="model-form-label">Capabilities</span>
        <div className="model-form-caps">
          {CAPABILITY_KEYS.map((c) => (
            <label key={c.key} className="model-cap-check">
              <input
                type="checkbox"
                checked={!!draft.capabilities?.[c.key]}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    capabilities: { ...draft.capabilities, [c.key]: e.target.checked },
                  })
                }
              />
              {c.label}
            </label>
          ))}
        </div>
      </div>
      {formError && <div className="model-form-error">{formError}</div>}
      <div className="model-form-actions">
        <button className="model-form-cancel" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button className="model-form-save" onClick={submit} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function RefreshReportView({ report }: { report: RefreshReport }) {
  const when = new Date(report.generatedAt).toLocaleString();
  return (
    <div className="refresh-report">
      <div className="refresh-report-head">Last refresh: {when}</div>
      <div className="refresh-report-rows">
        {report.providers.map((p) => (
          <div key={p.provider} className={`refresh-report-row status-${p.status}`}>
            <span className="refresh-report-provider">{p.provider}</span>
            <span className="refresh-report-status">
              {p.status === 'ok'
                ? `${p.added ?? 0} added / ${p.fetched ?? 0} found`
                : p.status}
            </span>
            {p.message && <span className="refresh-report-msg">{p.message}</span>}
          </div>
        ))}
      </div>
      {report.pricing && (
        <div className="refresh-report-pricing">
          Priced {report.pricing.matched} models ({report.pricing.fromOpenRouter} from OpenRouter,{' '}
          {report.pricing.fromFallback} from fallback).
          {report.pricing.unmatched.length > 0 &&
            ` ${report.pricing.unmatched.length} without a price.`}
        </div>
      )}
    </div>
  );
}

interface OpenRouterPickerProps {
  catalog: OpenRouterCatalogEntry[];
  existingIds: Set<string>;
  onCancel: () => void;
  onPick: (id: string, displayName?: string) => Promise<void>;
}

function OpenRouterPicker({ catalog, existingIds, onCancel, onPick }: OpenRouterPickerProps) {
  const [query, setQuery] = useState<string>('');
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const available = catalog.filter((c) => !existingIds.has(c.id));
    if (!q) return available.slice(0, 30);
    return available
      .filter((c) => c.id.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
      .slice(0, 30);
  }, [catalog, existingIds, query]);

  const pick = async (entry: OpenRouterCatalogEntry): Promise<void> => {
    setError(null);
    setAdding(entry.id);
    try {
      await onPick(entry.id, entry.name);
    } catch (err) {
      setError((err as Error).message || 'Add failed.');
      setAdding(null);
    }
  };

  return (
    <div className="model-form openrouter-picker">
      <div className="model-form-row">
        <label className="model-form-label">Search OpenRouter</label>
        <input
          type="text"
          className="model-form-input"
          value={query}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
          placeholder={catalog.length ? 'e.g. gpt-5, claude, llama…' : 'Run Refresh Models first to load the catalog'}
        />
      </div>
      {error && <div className="model-form-error">{error}</div>}
      {catalog.length === 0 ? (
        <div className="model-empty">No catalog cached yet. Click Refresh Models to pull it.</div>
      ) : (
        <div className="openrouter-results">
          {results.length === 0 && <div className="model-empty">No matches.</div>}
          {results.map((c) => (
            <div key={c.id} className="openrouter-result">
              <div className="openrouter-result-main">
                <span className="model-name">{c.name}</span>
                <span className="model-id">{c.id}</span>
              </div>
              <div className="openrouter-result-price">
                <span className="model-price-val">in {fmtPrice(c.input)}</span>
                <span className="model-price-val">out {fmtPrice(c.output)}</span>
              </div>
              <button
                className="model-add-btn"
                disabled={adding !== null}
                onClick={() => pick(c)}
              >
                {adding === c.id ? 'Adding…' : 'Add'}
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="model-form-actions">
        <button className="model-form-cancel" onClick={onCancel} disabled={adding !== null}>
          Close
        </button>
      </div>
    </div>
  );
}

/* ---------- Cost Meter ---------- */

interface CostMeterSectionProps {
  costReport: CostReport | null;
}

function fmtUsd(n: number | undefined): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

function fmtInt(n: number | undefined): string {
  return (n ?? 0).toLocaleString();
}

function CostMeterSection({ costReport }: CostMeterSectionProps) {
  if (!costReport) return null;
  const { today, total, day } = costReport;

  const columns: { label: string; bucket: CostReport['today'] }[] = [
    { label: `Today${day ? ` (${day})` : ''}`, bucket: today },
    { label: 'All time', bucket: total },
  ];

  return (
    <section className="settings-section">
      <h2 className="section-title">API Spend</h2>
      <div className="cost-grid">
        {columns.map((col) => (
          <div key={col.label} className="cost-card">
            <div className="cost-card-label">{col.label}</div>
            <div className="cost-usd">{fmtUsd(col.bucket?.usd)}</div>
            <div className="cost-meta">
              <span>{fmtInt(col.bucket?.calls)} calls</span>
              <span>{fmtInt(col.bucket?.input_tokens)} in</span>
              <span>{fmtInt(col.bucket?.output_tokens)} out</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Routing Presets ---------- */

interface RoutingPresetsSectionProps {
  presets: RoutingPresets;
  error: string | null;
  onSave: (name: string) => Promise<void>;
  onApply: (name: string) => Promise<void>;
  onDelete: (name: string) => Promise<void>;
}

function RoutingPresetsSection({ presets, error, onSave, onApply, onDelete }: RoutingPresetsSectionProps) {
  const [name, setName] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const names = Object.keys(presets);

  const save = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await onSave(trimmed);
      setName('');
    } catch {
      // error surfaced via store
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="models-subsection">
      <h3 className="subsection-title">Routing Presets</h3>
      <p className="settings-help">
        Save the current task assignments as a named preset, then apply it later in one click.
      </p>
      {error && <div className="model-form-error">{error}</div>}

      <div className="preset-save-row">
        <input
          type="text"
          className="model-form-input"
          value={name}
          placeholder="Preset name (e.g. Cheap draft)"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        />
        <button className="model-form-save" onClick={save} disabled={busy || !name.trim()}>
          Save current
        </button>
      </div>

      <div className="preset-list">
        {names.length === 0 && (
          <div className="model-empty">No presets saved yet.</div>
        )}
        {names.map((n) => (
          <div key={n} className="preset-row">
            <span className="preset-name">{n}</span>
            <span className="preset-count">{Object.keys(presets[n] || {}).length} tasks</span>
            <div className="preset-actions">
              <button className="model-edit-btn" onClick={() => onApply(n)}>Apply</button>
              <button className="model-delete-btn" onClick={() => onDelete(n)}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Word Bank auto-chain ---------- */

// KV wordbank.auto_chain_enabled, default off (Odin's decision 2026-09-26).
// Off: a Word Bank bucket move saves the move and its history, nothing else.
function WordBankAutoChainToggle() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    api.get('/api/config/wordbank-auto-chain')
      .then((d) => setEnabled((d as { enabled: boolean }).enabled))
      .catch((e: Error) => setError(e.message));
  }, []);

  const change = async (next: boolean): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const d = await api.put('/api/config/wordbank-auto-chain', { enabled: next }) as { enabled: boolean };
      setEnabled(d.enabled);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-field">
      {error && <div className="model-form-error">{error}</div>}
      <label className="settings-toggle">
        <input
          type="checkbox"
          checked={enabled === true}
          disabled={saving || enabled === null}
          onChange={(e) => void change(e.target.checked)}
        />
        Run audit and Stage 1 automatically after bucket moves
      </label>
      <p className="settings-help">Warning: when on, every move to Game or Related generates content and spends on model calls. Off by default.</p>
    </div>
  );
}

/* ---------- Default directions ---------- */

// Odin's ruling 2026-10-31: one standing direction per Your direction box (KV
// direction_defaults). A word whose box has no note of its own is generated
// with the default; a note saved on the word wins. A save changes only later
// generates. The retired Hook has no default.
type DefaultKind = 'definition' | 'fakes' | 'reveal' | 'assign' | 'examples' | 'howtouse' | 'funfacts' | 'reframes';
const DEFAULT_LABELS: Record<DefaultKind, string> = {
  definition: '1 Meaning: enhanced definition',
  fakes: '2 Fakes',
  reveal: '3 Reveals: reveal lines and tags',
  assign: '4 Assignment',
  examples: '5 Examples: example sentences',
  howtouse: '5 Examples: how to use',
  funfacts: '5 Examples: fun facts',
  reframes: '6 Win/Lose: reframes',
};

function DirectionDefaultsSection() {
  const [saved, setSaved] = useState<Record<string, string> | null>(null);
  const [kinds, setKinds] = useState<DefaultKind[]>([]);
  const [max, setMax] = useState<number>(500);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    api.get('/api/config/direction-defaults')
      .then((d) => {
        const r = d as { defaults: Record<string, string>; kinds: DefaultKind[]; max: number };
        setSaved(r.defaults); setDraft(r.defaults); setKinds(r.kinds); setMax(r.max);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const norm = (v: string | undefined): string => (v ?? '').replace(/\s+/g, ' ').trim();
  const dirty = saved !== null && kinds.some((k) => norm(draft[k]) !== norm(saved[k]));

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const d = await api.put('/api/config/direction-defaults', { defaults: draft }) as { defaults: Record<string, string> };
      setSaved(d.defaults); setDraft(d.defaults); setSavedAt(Date.now());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-section">
      <h2 className="section-title">Default directions</h2>
      <p className="section-desc">
        Sent with every Generate and Regenerate of a word whose Your direction box has no note of its own.
        A note set on a word wins over its default. A change here affects only later generates; nothing already generated changes.
        An empty box sends no default.
      </p>
      {error && <div className="model-form-error">{error}</div>}
      {saved === null && !error && <div className="model-empty">Loading...</div>}
      {saved !== null && kinds.map((k) => (
        <div className="settings-field" key={k}>
          <label className="settings-field-label" htmlFor={`direction-default-${k}`}>{DEFAULT_LABELS[k] ?? k}</label>
          <textarea
            id={`direction-default-${k}`} className="direction-default-input" rows={3} maxLength={max}
            value={draft[k] ?? ''} disabled={saving}
            onChange={(e) => { setDraft({ ...draft, [k]: e.target.value.replace(/[\r\n]+/g, ' ') }); setSavedAt(null); }}
          />
        </div>
      ))}
      {saved !== null && (
        <div className="model-form-actions">
          {savedAt && !dirty && <span className="settings-help">Saved.</span>}
          {dirty && <button className="model-edit-btn" disabled={saving} onClick={() => setDraft(saved)}>Cancel</button>}
          <button className="model-form-save" onClick={() => void save()} disabled={saving || !dirty}>
            {saving ? 'Saving...' : 'Save defaults'}
          </button>
        </div>
      )}
    </section>
  );
}

/* ---------- QA Banned Phrases ---------- */

// Social Engine scheduler mode (Chunk 7). Dry run builds and saves the payload
// and sends nothing. Live has no enabled scheduler yet, so the Worker refuses to
// schedule in live mode. This is the only place the mode can change.
function SocialScheduleModeSection() {
  const [mode, setMode] = useState<'dry_run' | 'live' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    api.get('/api/config/social-schedule-mode')
      .then((d) => setMode((d as { mode: 'dry_run' | 'live' }).mode))
      .catch((e: Error) => setError(e.message));
  }, []);

  const change = async (next: 'dry_run' | 'live'): Promise<void> => {
    if (next === 'live' && !window.confirm('Switch social scheduling to live? No live scheduler is enabled yet, so scheduling will be refused until one is.')) return;
    setSaving(true);
    setError(null);
    try {
      const d = await api.put('/api/config/social-schedule-mode', { mode: next }) as { mode: 'dry_run' | 'live' };
      setMode(d.mode);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-section">
      <h2 className="section-title">Social Scheduler</h2>
      {error && <div className="model-form-error">{error}</div>}
      <div className="settings-field">
        <label className="settings-toggle">
          <input type="radio" name="social-schedule-mode" checked={mode === 'dry_run'} disabled={saving || mode === null} onChange={() => void change('dry_run')} />
          Dry run (default): build and save what would be sent. Nothing is sent.
        </label>
        <label className="settings-toggle">
          <input type="radio" name="social-schedule-mode" checked={mode === 'live'} disabled={saving || mode === null} onChange={() => void change('live')} />
          Live: no scheduler is enabled yet, so scheduling is refused.
        </label>
      </div>
    </section>
  );
}

interface QaBannedPhrasesSectionProps {
  bannedPhrases: string[];
  error: string | null;
  onSave: (bannedPhrases: string[]) => Promise<void>;
}

function QaBannedPhrasesSection({ bannedPhrases, error, onSave }: QaBannedPhrasesSectionProps) {
  const [phrases, setPhrases] = useState<string[]>(bannedPhrases);
  const [draft, setDraft] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Keep local list in sync when the store loads/refreshes.
  useEffect(() => {
    setPhrases(bannedPhrases);
  }, [bannedPhrases]);

  const dirty = useMemo(
    () => JSON.stringify(phrases) !== JSON.stringify(bannedPhrases),
    [phrases, bannedPhrases]
  );

  const add = (): void => {
    const p = draft.trim();
    if (!p || phrases.includes(p)) { setDraft(''); return; }
    setPhrases([...phrases, p]);
    setDraft('');
  };

  const remove = (p: string): void => {
    setPhrases(phrases.filter((x) => x !== p));
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await onSave(phrases);
      setSavedAt(Date.now());
    } catch {
      // error surfaced via store
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-section">
      <h2 className="section-title">QA Banned Phrases</h2>
      <p className="section-desc">
        Phrases flagged by the review-time QA linter. Content containing any of these is called out for edit.
      </p>
      {error && <div className="model-form-error">{error}</div>}

      <div className="preset-save-row">
        <input
          type="text"
          className="model-form-input"
          value={draft}
          placeholder="Add a banned phrase"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
        />
        <button className="model-add-btn" onClick={add} disabled={!draft.trim()}>
          + Add
        </button>
      </div>

      <div className="banned-phrase-list">
        {phrases.length === 0 && (
          <div className="model-empty">No banned phrases configured.</div>
        )}
        {phrases.map((p) => (
          <span key={p} className="banned-phrase-chip">
            {p}
            <button className="banned-phrase-remove" onClick={() => remove(p)} aria-label={`Remove ${p}`}>
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="model-form-actions">
        {savedAt && !dirty && <span className="settings-help">Saved.</span>}
        <button className="model-form-save" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving...' : 'Save phrases'}
        </button>
      </div>
    </section>
  );
}
